/**
 * Expedient status values
 */
const EXPEDIENT_STATUSES = {
  ON_GOING: 'On going',
  EMAIL_OPENED: 'Email opened',
  BOUNCED: 'Bounced',
  HOT: 'Hot',
  COLD: 'Cold',
  FALLIDO: 'Fallido',
  ILOCALIZABLE: 'Ilocalizable',
  SIN_DATOS_CONTACTO: 'Sin datos de contacto',
  CANCELLED: 'Cancelled',
  DISPUTE: 'Dispute',
  BUROFAX_ENVIADO: 'Burofax Enviado',
  BUROFAX_RESULTADO_NEGATIVO: 'Burofax con resultado negativo',
  BUROFAX_RESULTADO_POSITIVO: 'Burofax con resultado positivo',
  DEMANDA_PENDIENTE: 'Demanda pendiente',
  DEMANDA_PREPARADA: 'Demanda preparada',
  DEMANDA_PRESENTADA: 'Demanda presentada',
  DEMANDA_ADMITIDA: 'Demanda Admitida',
  DEMANDA_NO_ADMITIDA: 'Demanda No Admitida',
  OPOSICION_DEUDOR: 'Oposición del Deudor',
  PENDIENTE_REQUERIMIENTO: 'Pendiente Requerimiento',
  REQUERIMIENTO_JUDICIAL_POSITIVO: 'Requerimiento Judicial Positivo',
  PENDIENTE_EJECUCION: 'Pendiente Ejecucion',
  SENTENCIA_ESTIMATORIA: 'Sentencia Estimatoria',
  SENTENCIA_DESESTIMATORIA: 'Sentencia Desestimatoria',
  SENTENCIA_PARCIAL: 'Sentencia Parcial',
  COBRADO: 'Cobrado',
  COBRADO_JUDICIALMENTE: 'Cobrado Judicialmente',
  COBRADO_EXTRAJUDICIALMENTE: 'Cobrado Extrajudicialmente',
  COBRO_PARCIAL_JUDICIAL: 'Cobro parcial judicial',
  // El pago parcial extrajudicial SIN plan acordado. Hasta el 23/10/2026 esto
  // era `Plan de pagos`; ver docs/planes-de-pago-fase-1-plan.md.
  COBRO_PARCIAL_EXTRAJUDICIAL: 'Cobro parcial extrajudicial',
  ILOCALIZABLE_JUDICIALMENTE: 'Ilocalizable Judicialmente',
  PLAN_DE_PAGOS: 'Plan de pagos',
};

/**
 * Cómo se ENSEÑA cada estado (27/10/2026). El valor guardado no cambia —lo leen
 * reglas, filtros, el agente y la API—: esto es solo la etiqueta en pantalla,
 * para que el cliente no vea «On going» o «Cold». Lo que no está aquí ya está en
 * castellano y se enseña tal cual (`expedientStatusLabel`).
 *
 * 28/10/2026 (Edgar): «Interesado» e «Intención de pago» son el mismo estado para el
 * cliente, y se queda el nombre «Intención de pago» (`Hot`, el que se asigna). `Cold`
 * pasa a «Sin intención de pago». `Payment Intent` es el viejo, que ya no asigna nadie:
 * lleva «(antiguo)» para no salir dos veces igual en el filtro.
 */
const EXPEDIENT_STATUS_LABELS = {
  'On going': 'En curso',
  'Email opened': 'Email abierto',
  Bounced: 'Email rebotado',
  Hot: 'Intención de pago',
  Cold: 'Sin intención de pago',
  Cancelled: 'Cancelado',
  Dispute: 'En disputa',
  'Burofax Enviado': 'Burofax enviado',
  'Demanda Admitida': 'Demanda admitida',
  'Demanda No Admitida': 'Demanda no admitida',
  'Oposición del Deudor': 'Oposición del deudor',
  'Pendiente Requerimiento': 'Pendiente de requerimiento',
  'Requerimiento Judicial Positivo': 'Requerimiento judicial positivo',
  'Pendiente Ejecucion': 'Pendiente de ejecución',
  'Sentencia Estimatoria': 'Sentencia estimatoria',
  'Sentencia Desestimatoria': 'Sentencia desestimatoria',
  'Sentencia Parcial': 'Sentencia parcial',
  'Cobrado Judicialmente': 'Cobrado judicialmente',
  'Cobrado Extrajudicialmente': 'Cobrado extrajudicialmente',
  'Ilocalizable Judicialmente': 'Ilocalizable judicialmente',
  'Payment Intent': 'Intención de pago (antiguo)',
};

/** La etiqueta de un estado; el valor tal cual si no tiene otra. */
const expedientStatusLabel = (estado) => EXPEDIENT_STATUS_LABELS[estado] || estado || '';

/**
 * All expedient statuses as an array
 */
const ALL_EXPEDIENT_STATUSES = Object.values(EXPEDIENT_STATUSES);

/**
 * Estados que ya no asigna nadie pero siguen en la base (30/09/2026: 24
 * `Payment Intent` y 2 `Preparando reclamación`). `Payment Intent` lo escribía
 * `services/openai/statusDecisor.js`, que no carga nadie.
 *
 * 🔴 Van APARTE de `ALL_EXPEDIENT_STATUSES` a propósito: esa lista alimenta el
 * desplegable de edición y las reglas del despachador, y meterlos ahí los
 * volvería asignables. Aquí solo sirven para poder ENCONTRARLOS.
 */
const LEGACY_EXPEDIENT_STATUSES = ['Payment Intent', 'Preparando reclamación'];

/**
 * Todos los estados por los que se puede filtrar un listado: los vigentes más
 * los legados. Un expediente cuyo estado no esté aquí no se puede encontrar
 * filtrando, y no da ningún error: si aparece un estado nuevo en la base, va
 * en una de las dos listas de arriba.
 */
const FILTERABLE_EXPEDIENT_STATUSES = [...ALL_EXPEDIENT_STATUSES, ...LEGACY_EXPEDIENT_STATUSES];

/**
 * Los estados que solo pone el sistema, nunca una persona, el despacho ni el
 * agente (docs/planes-de-pago-fase-1-plan.md, 23/10/2026).
 *
 * `Plan de pagos` significa que hay un plan ACORDADO con cuotas: lo pondrá el
 * alta del plan y lo quitará su cancelación o su final. Si se pudiera poner a
 * mano volvería a significar «ha pagado algo», que es justo lo que se arregló
 * creando `Cobro parcial extrajudicial`.
 *
 * Dejarlo como estaba sí se puede: un formulario que reenvía el estado actual
 * no lo está «poniendo». Por eso se pregunta con `canSetStateManually(de, a)`,
 * no mirando si está en una lista.
 */
const SYSTEM_ONLY_STATES = [EXPEDIENT_STATUSES.PLAN_DE_PAGOS];

/** Los estados que una persona puede elegir en un desplegable. */
const MANUALLY_SETTABLE_STATES = ALL_EXPEDIENT_STATUSES.filter((s) => !SYSTEM_ONLY_STATES.includes(s));

/** ¿Se puede llevar a mano el expediente de `from` a `to`? Quedarse donde está, siempre. */
const canSetStateManually = (from, to) => to === from || !SYSTEM_ONLY_STATES.includes(to);

/**
 * Los dos estados del pago a plazos en la vía extrajudicial: el pago parcial
 * sin acuerdo y el plan acordado.
 *
 * 🔴 Todo lo que protege o prioriza a un deudor que está pagando (la cola fría,
 * la urgencia del WhatsApp, la salida de AR, el correo automático, dónde se
 * acumula la deuda nueva…) tiene que leer LOS DOS. Los 243 `Plan de pagos` de
 * producción pasan a `Cobro parcial extrajudicial` con la migración, y una
 * lista que solo tuviera el primero los dejaría sin protección de golpe.
 * `tests/constants/cobro-parcial-replica-plan.test.js` lo comprueba lista a lista.
 */
const PAYING_STATES = [EXPEDIENT_STATUSES.PLAN_DE_PAGOS, EXPEDIENT_STATUSES.COBRO_PARCIAL_EXTRAJUDICIAL];

/**
 * Finalized statuses — expedients considered closed for reporting
 */
const FINALIZED_STATUSES = [
  EXPEDIENT_STATUSES.COBRADO,
  EXPEDIENT_STATUSES.BOUNCED,
  EXPEDIENT_STATUSES.COBRADO_JUDICIALMENTE,
  EXPEDIENT_STATUSES.COBRADO_EXTRAJUDICIALMENTE,
  EXPEDIENT_STATUSES.CANCELLED,
  EXPEDIENT_STATUSES.FALLIDO,
  EXPEDIENT_STATUSES.SENTENCIA_DESESTIMATORIA,
  EXPEDIENT_STATUSES.ILOCALIZABLE,
  EXPEDIENT_STATUSES.SIN_DATOS_CONTACTO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_NEGATIVO,
  EXPEDIENT_STATUSES.DEMANDA_NO_ADMITIDA,
];

/**
 * Los estados en que un expediente TIENE GESTIÓN EN CURSO.
 *
 * Es una propiedad del expediente, no una acción. De ella cuelgan las dos
 * decisiones que preguntan lo mismo —«¿cuál de estos expedientes es el vivo?»—
 * y que por tanto tienen que contestar igual:
 *
 *   · **dónde se acumula una deuda nueva** del mismo deudor, en vez de abrir
 *     otro expediente (importadora y API pública);
 *   · **dónde cae un mensaje o correo ENTRANTE** cuando el deudor tiene varios.
 *
 * 🔴 Lo que NO es: la lista de «¿le mando un correo automático?». Esa es
 * `EXPEDIENT_TERMINAL_STATES` en `sendgrid.js`, es otra pregunta, y contesta
 * distinto a propósito — a un `Dispute` no se le manda correo automático, pero
 * su deuda nueva sí se acumula ahí y su respuesta sí cae ahí. Usar una lista
 * para las dos cosas es el error que esto viene a deshacer.
 *
 * 🔴 Y es una lista BLANCA, no negra (16/09/2026, decisión de Edgar). Antes se
 * acumulaba salvo que el estado estuviera en `EXPEDIENT_TERMINAL_STATES`, veinte
 * estados; una lista negra deja pasar todo lo que nadie pensó en meter, y daba
 * dos resultados malos:
 *
 *   · `Ilocalizable` y `Sin datos de contacto` acumulaban —1.106 expedientes—,
 *     metiendo la deuda nueva en uno al que no sabemos escribir en vez de abrir
 *     uno limpio con los datos de contacto que trae la importación;
 *   · `Dispute` abría expediente nuevo, y la disputa viva se quedaba huérfana en
 *     el viejo mientras la gestión seguía por el otro.
 *
 * Lo del entrante venía de otro sitio y acaba en la misma regla: `emparejar`
 * usaba un `findOne` sin ordenar, así que la respuesta de un deudor con dos
 * expedientes caía en el que Mongo devolviera primero —normalmente el más
 * antiguo, o sea el muerto—. Hay 2.085 teléfonos compartidos entre expedientes
 * distintos y, de 400 mirados, 106 tienen uno cerrado y otro vivo.
 */
const STATUSES_WITH_ACTIVE_MANAGEMENT = [
  EXPEDIENT_STATUSES.ON_GOING,
  EXPEDIENT_STATUSES.EMAIL_OPENED,
  EXPEDIENT_STATUSES.HOT,
  EXPEDIENT_STATUSES.COLD,
  EXPEDIENT_STATUSES.DISPUTE,
  // Un deudor que está pagando a plazos es la gestión más viva que hay: la
  // deuda nueva entra en su expediente, no en uno aparte que nadie relacionaría
  // con el plan que ya está cumpliendo. Lo mismo con el pago parcial sin plan.
  EXPEDIENT_STATUSES.PLAN_DE_PAGOS,
  EXPEDIENT_STATUSES.COBRO_PARCIAL_EXTRAJUDICIAL,
];

/**
 * Estados que NO cuentan en el panel de emails abiertos.
 *
 * 🔴 Separada de `EXPEDIENT_TERMINAL_STATES` el 16/09/2026, con el MISMO
 * contenido a propósito: el comportamiento no cambia hoy, lo que cambia es que
 * deja de estar atada a otra pregunta.
 *
 * Compartía lista con `sendgrid.js`, y son cosas distintas: allí se decide «¿le
 * mando un correo automático?» y aquí «¿cuento su email abierto como señal de
 * interés en el panel?». Hoy coinciden por casualidad; el día que una de las dos
 * tenga que cambiar —y a un `Dispute` se le deja de escribir pero su apertura sí
 * dice algo— habría que tocar una y la otra se movería sola, sin que nadie lo
 * pidiera y sin dar error.
 *
 * Si alguna vez divergen, esta es la que se toca para el panel.
 */
const STATUSES_EXCLUDED_FROM_OPENED_PANEL = [
  EXPEDIENT_STATUSES.COBRADO,
  EXPEDIENT_STATUSES.DISPUTE,
  EXPEDIENT_STATUSES.CANCELLED,
  EXPEDIENT_STATUSES.COBRADO_JUDICIALMENTE,
  EXPEDIENT_STATUSES.FALLIDO,
  EXPEDIENT_STATUSES.BUROFAX_ENVIADO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_NEGATIVO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_POSITIVO,
  EXPEDIENT_STATUSES.DEMANDA_PENDIENTE,
  EXPEDIENT_STATUSES.DEMANDA_PREPARADA,
  EXPEDIENT_STATUSES.DEMANDA_PRESENTADA,
  EXPEDIENT_STATUSES.DEMANDA_ADMITIDA,
  EXPEDIENT_STATUSES.DEMANDA_NO_ADMITIDA,
  EXPEDIENT_STATUSES.OPOSICION_DEUDOR,
  EXPEDIENT_STATUSES.PENDIENTE_REQUERIMIENTO,
  EXPEDIENT_STATUSES.REQUERIMIENTO_JUDICIAL_POSITIVO,
  EXPEDIENT_STATUSES.PENDIENTE_EJECUCION,
  EXPEDIENT_STATUSES.SENTENCIA_ESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_DESESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_PARCIAL,
];

/**
 * Estados a los que NO se manda correo automático (`sendgrid.js`).
 *
 * Desde el 16/09/2026 este es su ÚNICO consumidor de negocio: antes contestaba
 * también «¿dónde acumulo una deuda nueva?», «¿cuál es el expediente de esta
 * referencia?» y «¿cuento este email abierto?», que son cuatro preguntas
 * distintas con una sola respuesta. Cada una tiene ya su lista.
 */
const EXPEDIENT_TERMINAL_STATES = [
  EXPEDIENT_STATUSES.COBRADO,
  EXPEDIENT_STATUSES.DISPUTE,
  EXPEDIENT_STATUSES.CANCELLED,
  EXPEDIENT_STATUSES.COBRADO_JUDICIALMENTE,
  EXPEDIENT_STATUSES.FALLIDO,
  EXPEDIENT_STATUSES.BUROFAX_ENVIADO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_NEGATIVO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_POSITIVO,
  EXPEDIENT_STATUSES.DEMANDA_PENDIENTE,
  EXPEDIENT_STATUSES.DEMANDA_PREPARADA,
  EXPEDIENT_STATUSES.DEMANDA_PRESENTADA,
  EXPEDIENT_STATUSES.DEMANDA_ADMITIDA,
  EXPEDIENT_STATUSES.DEMANDA_NO_ADMITIDA,
  EXPEDIENT_STATUSES.OPOSICION_DEUDOR,
  EXPEDIENT_STATUSES.PENDIENTE_REQUERIMIENTO,
  EXPEDIENT_STATUSES.REQUERIMIENTO_JUDICIAL_POSITIVO,
  EXPEDIENT_STATUSES.PENDIENTE_EJECUCION,
  EXPEDIENT_STATUSES.SENTENCIA_ESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_DESESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_PARCIAL,
];

/**
 * Fase del expediente — eje ortogonal al `estado`. Sustituye al viejo
 * `tipo_reclamacion`, que se eliminó el 15/10/2026 (siempre decía «Extrajudicial»).
 * Representa el tipo de servicio contratado y gobierna canales y visibilidad.
 */
const EXPEDIENT_TYPES = {
  ACCOUNT_RECEIVABLE: 'account-receivable',
  JUDICIAL: 'judicial',
  EXTRA_JUDICIAL: 'extra-judicial',
};

/**
 * All expedient types as an array
 */
const ALL_EXPEDIENT_TYPES = Object.values(EXPEDIENT_TYPES);

/**
 * Fallback type del sistema cuando no hay columna del importador ni
 * company.service_type. El mundo legacy (recuperación de deuda) es extrajudicial.
 */
const DEFAULT_EXPEDIENT_TYPE = EXPEDIENT_TYPES.EXTRA_JUDICIAL;

/**
 * Tipos de expediente EXCLUIDOS de todo envío automático de email al deudor,
 * venga del origen que venga (cron `actionDecisor` y AI Agent). El email
 * automático es exclusivo de extrajudicial; los Accounts Receivable y judiciales
 * no reciben emails automáticos (para AR, hasta que exista la integración de
 * email por company). Nota: `$nin` con este array preserva los legacy sin `type`
 * (extrajudiciales de facto).
 */
const AUTO_EMAIL_EXCLUDED_TYPES = [
  EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE,
  EXPEDIENT_TYPES.JUDICIAL,
];

/**
 * Cómo se ENSEÑA cada fase. El valor guardado no cambia (29/10/2026, Edgar):
 * AR es «Gestión propia» (la cartera la trabaja el cliente) y el extrajudicial,
 * «Extrajudicial (recobro externo)» (la trabaja ClaimUp).
 */
const EXPEDIENT_TYPE_LABELS = {
  'account-receivable': 'Gestión propia',
  judicial: 'Judicial',
  'extra-judicial': 'Extrajudicial (recobro externo)',
};

/**
 * Alias aceptados al normalizar el tipo (p.ej. columna del importador), además
 * de los valores canónicos. Se comparan tras normalizar (lower + trim +
 * `_`/espacios → `-`). Incluye las etiquetas ES que ve el usuario en la app.
 */
const EXPEDIENT_TYPE_ALIASES = {
  'accounts-receivable': 'account-receivable',
  ar: 'account-receivable',
  'gestion-propia': 'account-receivable',
  'gestión-propia': 'account-receivable',
  extrajudicial: 'extra-judicial',
  'recobro-externo': 'extra-judicial',
  'extrajudicial-(recobro-externo)': 'extra-judicial',
};

/**
 * Default state for newly created expedients
 */
const DEFAULT_EXPEDIENT_STATE = EXPEDIENT_STATUSES.ON_GOING;

/**
 * Default currency for expedients
 */
const DEFAULT_CURRENCY = 'EUR';

/**
 * Debt activity types for expedients
 */
const DEBT_ACTIVITY_TYPES = {
  DEBT: 'debt',
  DEBT_REDUCTION: 'debt_reduction',
  BOND: 'bond',
  CREDIT: 'credit',
  CANCELLATION: 'cancellation',
  EXTRAJUDICIAL_PAYMENT: 'extrajudicial_payment',
  COURT_ORDERED_PAYMENT: 'court_ordered_payment',
};

/**
 * Array of all debt activity type values
 */
const DEBT_ACTIVITY_TYPES_ARRAY = Object.values(DEBT_ACTIVITY_TYPES);

/**
 * Types that increase the debt (positive effect)
 */
const DEBT_POSITIVE_TYPES = ['debt'];

/**
 * Types that decrease the debt (negative effect)
 */
const DEBT_NEGATIVE_TYPES = [
  'debt_reduction',
  'bond',
  'credit',
  'cancellation',
  'extrajudicial_payment',
  'court_ordered_payment',
];

/**
 * Spanish labels for debt activity types
 */
const DEBT_ACTIVITY_LABELS = {
  debt: 'Deuda',
  debt_reduction: 'Quita',
  bond: 'Fianza',
  credit: 'Abono',
  cancellation: 'Cancelación',
  extrajudicial_payment: 'Pago extrajudicial',
  court_ordered_payment: 'Pago judicial',
};

/**
 * Estados que llevan un expediente a la vía judicial: al entrar en cualquiera
 * de ellos, su `type` pasa a `judicial` (ver `docs/partners-plan.md`, D2).
 *
 * 🔴 Es el DISPARADOR, no el filtro. Lo que decide si un despacho ve un
 * expediente es el `type`, no esta lista. El cambio es solo de ida: salir de
 * estos estados no devuelve el `type`, eso lo hace un admin a mano.
 *
 * Decidida por Edgar el 02/10/2026, por exclusión: es judicial todo estado que
 * no esté en `EXTRAJUDICIAL_STATES` (abajo). Las dos listas se reparten
 * `EXPEDIENT_STATUSES` entera, y un test lo comprueba: un estado nuevo tiene
 * que ir a una de las dos, no puede quedarse fuera sin que nadie lo decida.
 * El burofax cuenta como fase judicial.
 */
const STATES_THAT_IMPLY_JUDICIAL = [
  EXPEDIENT_STATUSES.BUROFAX_ENVIADO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_NEGATIVO,
  EXPEDIENT_STATUSES.BUROFAX_RESULTADO_POSITIVO,
  EXPEDIENT_STATUSES.DEMANDA_PENDIENTE,
  EXPEDIENT_STATUSES.DEMANDA_PREPARADA,
  EXPEDIENT_STATUSES.DEMANDA_PRESENTADA,
  EXPEDIENT_STATUSES.DEMANDA_ADMITIDA,
  EXPEDIENT_STATUSES.DEMANDA_NO_ADMITIDA,
  EXPEDIENT_STATUSES.OPOSICION_DEUDOR,
  EXPEDIENT_STATUSES.PENDIENTE_REQUERIMIENTO,
  EXPEDIENT_STATUSES.REQUERIMIENTO_JUDICIAL_POSITIVO,
  EXPEDIENT_STATUSES.PENDIENTE_EJECUCION,
  EXPEDIENT_STATUSES.SENTENCIA_ESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_DESESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_PARCIAL,
  EXPEDIENT_STATUSES.COBRADO_JUDICIALMENTE,
  EXPEDIENT_STATUSES.COBRO_PARCIAL_JUDICIAL,
  EXPEDIENT_STATUSES.ILOCALIZABLE_JUDICIALMENTE,
];

/**
 * Los estados que solo existen DESPUÉS de presentar la demanda. Los usa la
 * sincronización con Horvell (llegar a uno desde «Demanda pendiente» o
 * «Demanda preparada» es saltarse la presentación) y la facturación (un
 * expediente que ya estaba aquí al cargarlo no factura su demanda). Ver
 * docs/horvell-sync-plan.md.
 *
 * «Cancelled» y «Fallido» no están: se puede cancelar sin haber presentado.
 */
const STATES_AFTER_FILING = [
  EXPEDIENT_STATUSES.DEMANDA_ADMITIDA,
  EXPEDIENT_STATUSES.DEMANDA_NO_ADMITIDA,
  EXPEDIENT_STATUSES.OPOSICION_DEUDOR,
  EXPEDIENT_STATUSES.PENDIENTE_REQUERIMIENTO,
  EXPEDIENT_STATUSES.REQUERIMIENTO_JUDICIAL_POSITIVO,
  EXPEDIENT_STATUSES.PENDIENTE_EJECUCION,
  EXPEDIENT_STATUSES.SENTENCIA_ESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_DESESTIMATORIA,
  EXPEDIENT_STATUSES.SENTENCIA_PARCIAL,
  EXPEDIENT_STATUSES.COBRO_PARCIAL_JUDICIAL,
  EXPEDIENT_STATUSES.COBRADO_JUDICIALMENTE,
  EXPEDIENT_STATUSES.ILOCALIZABLE_JUDICIALMENTE,
];

/**
 * Los estados que la empresa y su gestor pueden poner a mano en un expediente AR
 * (docs/ar-estado-a-mano-plan.md, 23/10/2026): todos, también los de cobrado,
 * menos los judiciales, porque entrar en uno pasa el expediente a judicial y eso
 * es sacarlo de AR. Para eso está «Pasar a recobro».
 */
const AR_MANUAL_STATES = MANUALLY_SETTABLE_STATES.filter((s) => !STATES_THAT_IMPLY_JUDICIAL.includes(s));

/**
 * La otra mitad: los estados de la vía extrajudicial (Edgar, 02/10/2026).
 * Entrar en uno de estos no toca el `type`.
 */
const EXTRAJUDICIAL_STATES = [
  EXPEDIENT_STATUSES.ON_GOING,
  EXPEDIENT_STATUSES.EMAIL_OPENED,
  EXPEDIENT_STATUSES.BOUNCED,
  EXPEDIENT_STATUSES.HOT,
  EXPEDIENT_STATUSES.COLD,
  EXPEDIENT_STATUSES.FALLIDO,
  EXPEDIENT_STATUSES.ILOCALIZABLE,
  EXPEDIENT_STATUSES.SIN_DATOS_CONTACTO,
  EXPEDIENT_STATUSES.CANCELLED,
  EXPEDIENT_STATUSES.DISPUTE,
  EXPEDIENT_STATUSES.COBRADO,
  EXPEDIENT_STATUSES.COBRADO_EXTRAJUDICIALMENTE,
  EXPEDIENT_STATUSES.PLAN_DE_PAGOS,
  EXPEDIENT_STATUSES.COBRO_PARCIAL_EXTRAJUDICIAL,
];

/**
 * Estados a los que un despacho puede mover un expediente: los judiciales.
 * Ninguno saca el expediente de judicial: eso es decisión de ClaimUp.
 *
 * Hasta el 23/10/2026 incluía `Plan de pagos`, «que se acuerda también en vía
 * judicial». Se quitó: el judicial no tiene planes y ese estado ya solo lo
 * pone el plan (docs/planes-de-pago-fase-1-plan.md). El 19/10 ningún judicial
 * estaba en él.
 */
const PARTNER_SETTABLE_STATES = [...STATES_THAT_IMPLY_JUDICIAL];

module.exports = {
  AR_MANUAL_STATES,
  EXPEDIENT_STATUSES,
  ALL_EXPEDIENT_STATUSES,
  LEGACY_EXPEDIENT_STATUSES,
  FILTERABLE_EXPEDIENT_STATUSES,
  STATES_THAT_IMPLY_JUDICIAL,
  STATES_AFTER_FILING,
  EXTRAJUDICIAL_STATES,
  PARTNER_SETTABLE_STATES,
  SYSTEM_ONLY_STATES,
  MANUALLY_SETTABLE_STATES,
  canSetStateManually,
  PAYING_STATES,
  FINALIZED_STATUSES,
  EXPEDIENT_TERMINAL_STATES,
  STATUSES_WITH_ACTIVE_MANAGEMENT,
  STATUSES_EXCLUDED_FROM_OPENED_PANEL,
  EXPEDIENT_TYPES,
  ALL_EXPEDIENT_TYPES,
  DEFAULT_EXPEDIENT_TYPE,
  AUTO_EMAIL_EXCLUDED_TYPES,
  EXPEDIENT_TYPE_LABELS,
  EXPEDIENT_STATUS_LABELS,
  expedientStatusLabel,
  EXPEDIENT_TYPE_ALIASES,
  DEFAULT_EXPEDIENT_STATE,
  DEFAULT_CURRENCY,
  DEBT_ACTIVITY_TYPES,
  DEBT_ACTIVITY_TYPES_ARRAY,
  DEBT_POSITIVE_TYPES,
  DEBT_NEGATIVE_TYPES,
  DEBT_ACTIVITY_LABELS,
};
